import { useEffect, useState } from 'react';

import { IOffer } from '@rbi-ctg/menu';
import { IOfferFeedbackEntryFragment } from 'generated/rbi-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { UIPattern } from 'state/offers/types';

export interface IUsePromoCodeOfferProps {
  sortedOffers: IOffer[];
  offersFeedback: Record<string, IOfferFeedbackEntryFragment> | undefined;
}

export default function usePromoCodeOffers({
  sortedOffers = [],
  offersFeedback = {}
}: IUsePromoCodeOfferProps) {
  const enablePromoCodeOffers = useFlag(LaunchDarklyFlag.ENABLE_PROMO_CODE_OFFERS);
  const [promoCodeOffers, setPromoCodeOffers] = useState<IOffer[]>([]);

  // update promo code offers when sortedOffers changes
  useEffect(() => {
    if (enablePromoCodeOffers) {
      // only if promo code offers are enabled via LD
      // get all promo code offers
      const offers = sortedOffers.reduce((acc, offer: IOffer) => {
        if (offer.uiPattern === UIPattern.PROMO) {
          // ensure isRedeemable is properly set based on offersFeedback
          if (offersFeedback[offer._id]?.redemptionEligibility?.isRedeemable) {
            offer.isRedeemable = true;
          }
          acc.push(offer);
        }
        return acc;
      }, [] as IOffer[]);
      setPromoCodeOffers(offers);
    }
  }, [enablePromoCodeOffers, offersFeedback, sortedOffers]);

  /**
   * enable toggling isRedeemable true / false
   * so that we don't have to re-fetch offers
   * unless absolutely necessary
   */
  const togglePromoCodeOfferRedeemable = (offerId: string) => {
    const offerIdx = promoCodeOffers.findIndex((curOffer: IOffer) => curOffer._id === offerId);
    if (offerIdx === -1) {
      return;
    }
    const offer = promoCodeOffers[offerIdx];
    offer.isRedeemable = !offer.isRedeemable;
    const updatedOffers = [
      ...promoCodeOffers.slice(0, offerIdx),
      offer,
      ...promoCodeOffers.slice(offerIdx + 1)
    ];
    setPromoCodeOffers(updatedOffers);
  };

  return {
    promoCodeOffers,
    togglePromoCodeOfferRedeemable
  };
}
